import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { usePagination } from '../../hooks/usePagination';
import { formatMois, formatMontant, formatNombre, formatPourcent } from '../../lib/formats';
import { nomLisible, referenceLisible } from '../../lib/libelles';
import { cn } from '../../lib/utils';
import { Aide } from '../ui/aide';
import { BarrePagination } from '../ui/pagination-bar';
import { LoadingState } from '../ui/loading-state';

/**
 * Onglet « Bénéfice » de l'écran Argent (revue de l'écran Argent, lot 8).
 *
 * Une bande d'une ligne (recettes, charges, bénéfice ou perte, marge), puis
 * un seul tableau qui change de vue: par mois, par client, par convoi, par
 * camion, par poste de charge. Chaque vue a sa ligne de total sur toutes les
 * lignes (pas seulement la page), ses montants à droite, et une colonne %
 * avec une petite barre là où la part compte. Le graphique n'apparaît que
 * par mois, à partir de deux mois, avec le même périmètre que les totaux.
 */

export const VUES_BENEFICE = [
  { cle: 'mois', titre: 'Par mois' },
  { cle: 'client', titre: 'Par client' },
  { cle: 'convoi', titre: 'Par convoi' },
  { cle: 'camion', titre: 'Par camion' },
  { cle: 'poste', titre: 'Par poste' },
];

/** « CONVOI-2026-029EC6-0006 » → « CONV-0006 »: le code complet reste au survol. */
export const codeCourt = (code) => {
  const morceaux = String(code || '').split('-').filter(Boolean);
  if (morceaux.length <= 2) return String(code || '');
  return `${morceaux[0].slice(0, 4)}-${morceaux[morceaux.length - 1]}`;
};

const n = (valeur) => Number(valeur) || 0;
const pct = (part, tout) => (n(tout) ? (n(part) / n(tout)) * 100 : null);

const Barre = ({ valeur }) => (
  <span className="ml-2 hidden h-1.5 w-16 overflow-hidden rounded-full bg-muted align-middle sm:inline-block" aria-hidden="true">
    <span className="block h-full rounded-full bg-primary/70" style={{ width: `${Math.max(0, Math.min(100, valeur || 0))}%` }} />
  </span>
);

const moisCourt = (mois) => {
  const [annee, numero] = String(mois || '').split('-').map(Number);
  if (!annee || !numero) return '';
  return new Date(annee, numero - 1, 1).toLocaleDateString('fr-FR', { month: 'short' });
};

/** Recettes (vert) et charges (rouge) mois par mois; seulement avec au moins deux mois. */
export const GraphiqueMois = ({ mois, hauteur = 180 }) => {
  if (!mois || mois.length < 2) return null;
  const max = Math.max(1, ...mois.flatMap((m) => [n(m.revenue), n(m.expenses)]));
  // Barres plus larges quand il y a peu de mois: le graphique occupe sa carte.
  const barre = Math.max(14, Math.min(48, Math.floor(900 / (mois.length * 2.6))));
  const ecart = 10;
  const groupe = barre * 2 + ecart;
  const marge = { haut: 8, bas: 22 };
  const interieur = hauteur - marge.haut - marge.bas;
  const largeur = mois.length * (groupe + ecart) + ecart;
  return (
    <figure className="w-fit max-w-full overflow-x-auto rounded-xl border bg-card p-3" data-testid="graphique-mois">
      <svg width={largeur} height={hauteur} role="img" aria-label="Recettes et charges par mois">
        {mois.map((m, i) => {
          const x = ecart + i * (groupe + ecart);
          const hR = (n(m.revenue) / max) * interieur;
          const hC = (n(m.expenses) / max) * interieur;
          return (
            <g key={m.month}>
              <rect x={x} y={marge.haut + interieur - hR} width={barre} height={hR} fill="hsl(var(--success))" rx={2}><title>{`Recettes ${formatMontant(m.revenue)}`}</title></rect>
              <rect x={x + barre + 2} y={marge.haut + interieur - hC} width={barre} height={hC} fill="hsl(var(--destructive))" rx={2}><title>{`Charges ${formatMontant(m.expenses)}`}</title></rect>
              <text x={x + barre} y={hauteur - 6} textAnchor="middle" fontSize="10" fill="hsl(var(--muted-foreground))">{moisCourt(m.month)}</text>
            </g>
          );
        })}
      </svg>
      <figcaption className="mt-1 flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-success" /> Recettes</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-destructive" /> Charges</span>
      </figcaption>
    </figure>
  );
};

/** Les lignes et les colonnes de chaque vue, et leur total sur toutes les lignes. Pure. */
export function vueBenefice(vue, summary, { nomClient = (id, nom) => nomLisible(nom), convois = new Map() } = {}) {
  const recettes = n(summary?.revenue?.total);
  const charges = summary?.charges != null ? n(summary.charges) : recettes - n(summary?.margin?.net);
  if (vue === 'client') {
    const lignes = (summary?.by_client || []).map((c) => ({
      cle: c.client_id,
      libelle: nomClient(c.client_id, c.name),
      invoiced: n(c.invoiced ?? c.revenue),
      unbilled: n(c.unbilled),
      revenue: n(c.revenue),
      paid: n(c.paid),
      outstanding: n(c.outstanding),
    }));
    if (n(summary?.revenue?.station_sales)) {
      lignes.push({ cle: 'stations', libelle: 'Ventes des stations', invoiced: 0, unbilled: 0, revenue: n(summary.revenue.station_sales), paid: 0, outstanding: 0, horsClient: true });
    }
    lignes.sort((a, b) => b.revenue - a.revenue);
    lignes.forEach((l) => { l.part = pct(l.revenue, recettes); });
    const somme = (cle) => lignes.reduce((t, l) => t + l[cle], 0);
    return {
      lignes,
      colonnes: [
        { cle: 'invoiced', titre: 'Facturé', montant: true },
        { cle: 'unbilled', titre: 'Livré non facturé', montant: true, masquee: !somme('unbilled') },
        { cle: 'revenue', titre: 'Recettes', montant: true, fort: true },
        { cle: 'part', titre: '% recettes', part: true },
        { cle: 'paid', titre: 'Encaissé', montant: true, mobileCache: true },
        { cle: 'outstanding', titre: 'Reste dû', montant: true, mobileCache: true },
      ],
      total: { invoiced: somme('invoiced'), unbilled: somme('unbilled'), revenue: somme('revenue'), part: recettes ? 100 : null, paid: somme('paid'), outstanding: somme('outstanding') },
      nom: 'client',
    };
  }
  if (vue === 'convoi' || vue === 'camion') {
    const source = vue === 'convoi' ? summary?.by_convoi || [] : summary?.by_truck || [];
    const lignes = source.map((c) => {
      const revenue = n(c.revenue);
      const expenses = n(c.expenses);
      const estime = vue === 'convoi' ? c.margin == null : Boolean(c.estimated);
      const info = vue === 'convoi' ? convois.get(c.operation_id) : null;
      return {
        cle: c.operation_id || c.truck_id || c.plate,
        libelle: vue === 'convoi' ? codeCourt(referenceLisible(c.code, 'Convoi')) : (c.plate || 'Camion'),
        titre: vue === 'convoi' ? referenceLisible(c.code, 'Convoi') : c.plate,
        detail: vue === 'convoi' ? [info?.beneficiary_name, info?.route].filter(Boolean).join(' · ') : c.trips ? `${formatNombre(c.trips)} trajet${c.trips > 1 ? 's' : ''}` : '',
        revenue,
        expenses,
        margin: revenue - expenses,
        estime,
        marge: pct(revenue - expenses, revenue),
      };
    });
    lignes.sort((a, b) => b.margin - a.margin);
    const somme = (cle) => lignes.reduce((t, l) => t + l[cle], 0);
    const total = { revenue: somme('revenue'), expenses: somme('expenses') };
    total.margin = total.revenue - total.expenses;
    total.marge = pct(total.margin, total.revenue);
    total.estime = lignes.some((l) => l.estime);
    return {
      lignes,
      colonnes: [
        { cle: 'revenue', titre: 'Recettes', montant: true },
        { cle: 'expenses', titre: 'Dépenses', montant: true, mobileCache: true },
        { cle: 'margin', titre: 'Bénéfice', montant: true, fort: true, estimable: true },
        { cle: 'marge', titre: 'Marge', pourcent: true, mobileCache: true },
      ],
      total,
      nom: vue,
    };
  }
  if (vue === 'poste') {
    // Les pertes de carburant: le détail (litres, trajets) reste dans « Livraisons à contrôler ».
    const lignes = (summary?.by_category || []).map((p) => ({
      cle: p.key,
      libelle: p.label,
      amount: n(p.amount),
      part: pct(p.amount, charges),
      lien: p.key === 'PERTES_CARBURANT' ? { to: '/mission-integrity', texte: 'Livraisons à contrôler →' } : null,
    }));
    lignes.sort((a, b) => b.amount - a.amount);
    return {
      lignes,
      colonnes: [
        { cle: 'amount', titre: 'Montant', montant: true, fort: true },
        { cle: 'part', titre: '% des charges', part: true },
      ],
      total: { amount: lignes.reduce((t, l) => t + l.amount, 0), part: charges ? 100 : null },
      nom: 'poste',
    };
  }
  const lignes = (summary?.by_month || []).map((m) => ({
    cle: m.month,
    libelle: m.month === '?' ? 'Sans date' : formatMois(m.month),
    revenue: n(m.revenue),
    expenses: n(m.expenses),
    margin: n(m.margin ?? n(m.revenue) - n(m.expenses)),
    marge: pct(n(m.revenue) - n(m.expenses), m.revenue),
  })).sort((a, b) => String(b.cle).localeCompare(String(a.cle)));
  const somme = (cle) => lignes.reduce((t, l) => t + l[cle], 0);
  const total = { revenue: somme('revenue'), expenses: somme('expenses') };
  total.margin = total.revenue - total.expenses;
  total.marge = pct(total.margin, total.revenue);
  return {
    lignes,
    colonnes: [
      { cle: 'revenue', titre: 'Recettes', montant: true },
      { cle: 'expenses', titre: 'Charges', montant: true, mobileCache: true },
      { cle: 'margin', titre: 'Bénéfice', montant: true, fort: true },
      { cle: 'marge', titre: 'Marge', pourcent: true, mobileCache: true },
    ],
    total,
    nom: 'mois',
  };
}

const NOMS = { mois: 'Mois', client: 'Client', convoi: 'Convoi', camion: 'Camion', poste: 'Poste' };
const UNITES = { mois: ['mois', 'mois'], client: ['ligne', 'lignes'], convoi: ['convoi', 'convois'], camion: ['camion', 'camions'], poste: ['poste', 'postes'] };

const Cellule = ({ colonne, ligne, total }) => {
  const valeur = ligne[colonne.cle];
  if (colonne.part) {
    return valeur == null ? '' : <span className="inline-flex items-center justify-end">{formatPourcent(valeur, 1)}{!total && <Barre valeur={valeur} />}</span>;
  }
  if (colonne.pourcent) return valeur == null ? '—' : formatPourcent(valeur, 1);
  if (colonne.montant) {
    if (!valeur && !colonne.fort) return '';
    const texte = formatMontant(valeur);
    if (colonne.estimable && ligne.estime) return <span className="text-muted-foreground" title="Estimé: trajets pas encore terminés">≈ {texte}</span>;
    return texte;
  }
  return valeur;
};

export default function Benefice({ summary, chargement, erreur, filtres, convois, nomClient, lienRapport, periodeLibelle }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const vue = VUES_BENEFICE.some((v) => v.cle === searchParams.get('vue')) ? searchParams.get('vue') : 'mois';
  const changerVue = (cle) => setSearchParams((precedent) => {
    const suivant = new URLSearchParams(precedent);
    if (cle === 'mois') suivant.delete('vue');
    else suivant.set('vue', cle);
    return suivant;
  });
  const carteConvois = useMemo(() => new Map((convois || []).map((c) => [c.id, c])), [convois]);
  const donnees = useMemo(() => vueBenefice(vue, summary, { nomClient, convois: carteConvois }), [vue, summary, nomClient, carteConvois]);
  const pagination = usePagination(donnees.lignes, { cle: 'benefice', reinitialiser: vue });
  const recettes = n(summary?.revenue?.total);
  const charges = summary?.charges != null ? n(summary.charges) : recettes - n(summary?.margin?.net);
  const benefice = n(summary?.margin?.net);
  const marge = pct(benefice, recettes);
  const colonnes = donnees.colonnes.filter((c) => !c.masquee);
  const [unite, unites] = UNITES[donnees.nom];

  return (
    <div className="space-y-3">
      {filtres}
      {erreur}
      {!summary && chargement && <LoadingState shape="table" rows={6} columns={5} label="Chargement du bénéfice" />}
      {summary && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 rounded-xl border bg-card px-4 py-2.5 text-sm" data-testid="bande-benefice">
            <span><span className="text-muted-foreground">Recettes</span> <strong className="tabular-nums">{formatMontant(recettes)}</strong></span>
            <span><span className="text-muted-foreground">Charges</span> <strong className="tabular-nums">{formatMontant(charges)}</strong></span>
            <span>
              <span className="text-muted-foreground">{benefice < 0 ? 'Perte' : 'Bénéfice'}</span>{' '}
              <strong className={cn('tabular-nums', benefice > 0 && 'text-success', benefice < 0 && 'text-destructive')}>{formatMontant(Math.abs(benefice))}</strong>
              {marge != null && <span className="text-muted-foreground"> ({formatPourcent(marge, 1)})</span>}
            </span>
            <Aide titre="Bénéfice de la période">
              {`${periodeLibelle ? `${periodeLibelle} · ` : ''}Recettes : factures, livraisons pas encore facturées et ventes des stations. Charges : frais de route, autres frais, pertes de carburant et carburant vendu à son coût d’achat (le carburant acheté et encore en stock n’est pas une charge). Bénéfice = recettes − charges.`}
            </Aide>
            {lienRapport && <Link to={lienRapport} className="ml-auto text-xs font-medium text-primary hover:underline">Rapport du mois →</Link>}
          </div>

          <div className="flex gap-1 overflow-x-auto" role="group" aria-label="Vue du bénéfice">
            {VUES_BENEFICE.map((v) => (
              <button
                key={v.cle}
                type="button"
                aria-pressed={vue === v.cle}
                onClick={() => changerVue(v.cle)}
                className={cn('inline-flex h-8 shrink-0 items-center rounded-full border px-3 text-xs font-medium transition-colors', vue === v.cle ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:bg-muted')}
              >
                {v.titre}
              </button>
            ))}
          </div>

          {vue === 'mois' && <GraphiqueMois mois={[...(summary.by_month || [])].filter((m) => m.month !== '?').sort((a, b) => String(a.month).localeCompare(String(b.month)))} />}

          <div className="overflow-x-auto rounded-xl border bg-card">
            <table className="w-full text-sm" data-testid={`benefice-${vue}`}>
              <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold">{NOMS[donnees.nom]}</th>
                  {colonnes.map((c) => (
                    <th key={c.cle} className={cn('whitespace-nowrap px-3 py-2 text-right font-semibold', c.mobileCache && 'hidden sm:table-cell')}>{c.titre}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {donnees.lignes.length === 0 && (
                  <tr className="h-10"><td colSpan={colonnes.length + 1} className="px-3 py-2 text-muted-foreground">Rien sur la période choisie.</td></tr>
                )}
                {pagination.lignes.map((ligne) => (
                  <tr key={ligne.cle} className="border-b last:border-0">
                    <td className="w-full max-w-0 px-3 py-1.5">
                      <span className={cn('block truncate', ligne.horsClient && 'italic text-muted-foreground')} title={ligne.titre || ligne.libelle}>{ligne.libelle}</span>
                      {ligne.detail && <span className="block truncate text-xs text-muted-foreground">{ligne.detail}</span>}
                      {ligne.lien && <Link to={ligne.lien.to} className="block text-xs font-medium text-primary hover:underline">{ligne.lien.texte}</Link>}
                    </td>
                    {colonnes.map((c) => (
                      <td key={c.cle} className={cn('whitespace-nowrap px-3 py-1.5 text-right tabular-nums', c.mobileCache && 'hidden sm:table-cell', c.fort && 'font-medium', c.cle === 'margin' && !ligne.estime && ligne.margin < 0 && 'text-destructive')}>
                        <Cellule colonne={c} ligne={ligne} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {donnees.lignes.length > 0 && (
                <tfoot className="border-t bg-muted/50 font-semibold" data-testid="benefice-total">
                  <tr>
                    <td className="px-3 py-2">{`Total (${formatNombre(donnees.lignes.length)} ${donnees.lignes.length > 1 ? unites : unite})`}</td>
                    {colonnes.map((c) => (
                      <td key={c.cle} className={cn('whitespace-nowrap px-3 py-2 text-right tabular-nums', c.mobileCache && 'hidden sm:table-cell')}>
                        <Cellule colonne={c} ligne={donnees.total} total />
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <BarrePagination pagination={pagination} nom="lignes" />
        </>
      )}
    </div>
  );
}
